import {useCallback, useEffect, useRef, useState} from 'react';
import {generatePath, useLocation, useNavigate} from 'react-router';
import {type InfiniteData, useQueryClient} from '@tanstack/react-query';
import {routes} from '../../../routing/routes';
import {
    type ADKEvent,
    type ADKSession,
    type AgentOption,
    type BackendChatSession,
    type ChatMessage,
    type ChatSession,
    CONSOLIDATION_PROGRESS_STATE_KEY,
    type ConsolidationToolProgress,
    type EmbeddedDashboard,
    type EmbeddedDashboardView,
    type SessionListResponse,
} from '../types/chatTypes';
import {useStores} from "../../../stores";
import {CHAT_CONFIG} from "../services/ChatConfiguration";
import {isDashgenAppName} from "../utils/agentDatabag";
import {sessionListQueryKey, useSessionListQuery} from "./useSessionListQuery";

const CONSOLIDATION_TOOL_NAMES = new Set(['consolidate_suppliers', 'consolidate_all_tail_suppliers']);
const CHAT_PATH_RE = /\/chat\/?$/;

function mapBackendSession(bs: BackendChatSession): ChatSession {
    return {
        id: bs.session_id,
        userId: bs.user_id,
        name: bs.display_name || 'New chat',
        messages: [],
        created_at: new Date(bs.last_update_time),
        updated_at: new Date(bs.last_update_time),
        appName: bs.app_name,
        dashboard: bs.dashboard ?? null,
    };
}

function searchWithSessionId(currentSearch: string, sessionId: string | null): string | undefined {
    const next = new URLSearchParams(currentSearch);
    const current = next.get('session_id');
    if (sessionId) {
        if (current === sessionId) {
            return undefined;
        }
        next.set('session_id', sessionId);
    } else {
        if (!current) {
            return undefined;
        }
        next.delete('session_id');
    }
    const query = next.toString();
    return query ? `?${query}` : '';
}

function getActionsStateDelta(event: ADKEvent): Record<string, unknown> | undefined {
    const a = event.actions;
    if (!a || typeof a !== 'object') return undefined;
    const raw =
        (a as {stateDelta?: unknown; state_delta?: unknown}).stateDelta
        ?? (a as {state_delta?: unknown}).state_delta;
    if (!raw || typeof raw !== 'object') return undefined;
    return raw as Record<string, unknown>;
}

/** Parses the `embedded` object from a dashgen tool response, if present and well-formed. */
function parseEmbeddedDashboard(response: unknown): EmbeddedDashboard | undefined {
    if (response == null || typeof response !== 'object') return undefined;
    const embedded = (response as {embedded?: unknown}).embedded;
    if (embedded == null || typeof embedded !== 'object') return undefined;
    const o = embedded as Record<string, unknown>;
    if (typeof o.uuid !== 'string' || typeof o.superset_domain !== 'string') return undefined;
    return {
        uuid: o.uuid,
        superset_domain: o.superset_domain,
        dashboard_id: Number(o.dashboard_id) || 0,
        title: typeof o.title === 'string' ? o.title : '',
    };
}

/** Latest message carrying an embedded dashboard, if any. */
function findLatestEmbeddedMessage(messages: ChatMessage[]): ChatMessage | null {
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].embedded) return messages[i];
    }
    return null;
}

function toEmbeddedView(embedded: EmbeddedDashboard | undefined | null): EmbeddedDashboardView | null {
    return embedded ? {...embedded, refreshToken: 0} : null;
}

function findLatestConsolidationCallIndex(messages: ChatMessage[]): number {
    for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.event_type === 'function_call' && CONSOLIDATION_TOOL_NAMES.has(m.content)) {
            return i;
        }
    }
    return -1;
}

function applyConsolidationStateDelta(messages: ChatMessage[], delta: Record<string, unknown>): ChatMessage[] {
    const keyPayload = delta[CONSOLIDATION_PROGRESS_STATE_KEY];
    if (keyPayload == null || typeof keyPayload !== 'object') {
        return messages;
    }
    const o = keyPayload as Record<string, unknown>;
    const idx = findLatestConsolidationCallIndex(messages);
    if (idx < 0) {
        return messages;
    }
    if (o.stage === 'done') {
        const next = [...messages];
        const nextMsg = {...next[idx]};
        delete nextMsg.toolProgress;
        next[idx] = nextMsg;
        return next;
    }
    const num = (v: unknown): number | undefined =>
        typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
    const toolProgress: ConsolidationToolProgress = {
        stage: String(o.stage ?? ''),
        completed: Number(o.completed) || 0,
        total: Math.max(Number(o.total) || 1, 1),
        detail: typeof o.detail === 'string' ? o.detail : undefined,
        stageNumber: num(o.stage_number) ?? num((o as {stageNumber?: unknown}).stageNumber),
        stageTotal: num(o.stage_total) ?? num((o as {stageTotal?: unknown}).stageTotal) ?? 4,
    };
    const next = [...messages];
    next[idx] = {...next[idx], toolProgress};
    return next;
}

// TODO: QA: Limit file size to ~300 lines
// TODO: QA: Separate communication layer and UI management
// TODO: QA: This component is not responding to muix updated for some unkown reason.
//  Therefor `bagStore.bagId` updates are being propagated as arguments and observables to ensure startNewChat() works from external components as well

// Do not import directly, use useChatContext (TODO: refactor to remove 'export')
export const useChat = () => {
    const {bagStore, chatApiStore} = useStores();
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const locationRef = useRef(location);
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const sessionsRef = useRef(sessions);
    const {
        data: sessionListData,
        isPending: sessionsPending,
        isFetched: sessionsListReady,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useSessionListQuery(bagStore.bagId || undefined);
    const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
    const [hasReceivedFinalMessage, setHasReceivedFinalMessage] = useState(true);
    const hasReceivedFinalMessageRef = useRef(hasReceivedFinalMessage);
    const [isRestoringSession, setIsRestoringSession] = useState(false);
    const [availableAgents, setAvailableAgents] = useState<AgentOption[]>([]);
    const [selectedAppName, setSelectedAppNameState] = useState<string>(CHAT_CONFIG.APP_NAME);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const currentSessionRef = useRef<ChatSession | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    /** Last session_id restored from the URL — avoids duplicate fetches, reset when URL clears. */
    const lastRestoredUrlSessionIdRef = useRef<string | null>(null);
    const prevBagIdRef = useRef(0);
    const isWaiting = Boolean(currentSession?.messages.length) && !hasReceivedFinalMessage;
    /** Superset dashboard shown in the right-side panel (latest from the agent). */
    const [embeddedDashboard, setEmbeddedDashboard] = useState<EmbeddedDashboardView | null>(null);
    const [syncedSessionListData, setSyncedSessionListData] = useState(sessionListData);
    const [syncedBagId, setSyncedBagId] = useState(bagStore.bagId);

    // Keep refs in sync for async callbacks without reading them during render.
    useEffect(() => {
        locationRef.current = location;
    }, [location]);
    useEffect(() => {
        sessionsRef.current = sessions;
    }, [sessions]);
    useEffect(() => {
        hasReceivedFinalMessageRef.current = hasReceivedFinalMessage;
    }, [hasReceivedFinalMessage]);
    useEffect(() => {
        currentSessionRef.current = currentSession;
    }, [currentSession]);

    const syncSessionToUrl = useCallback((sessionId: string | null) => {
        const {pathname, search} = locationRef.current;
        const nextSearch = searchWithSessionId(search, sessionId);
        if (nextSearch === undefined) {
            return;
        }

        let pathnameTarget = pathname;
        if (sessionId && !CHAT_PATH_RE.test(pathname)) {
            const bagId = bagStore.bagId;
            if (!bagId) {
                return;
            }
            pathnameTarget = generatePath(routes.chat, {id: String(bagId)});
        } else if (!sessionId && !CHAT_PATH_RE.test(pathname)) {
            return;
        }

        navigate({pathname: pathnameTarget, search: nextSearch}, {replace: true});
    }, [navigate, bagStore.bagId]);

    const setSelectedAppName = useCallback((appName: string) => {
        setSelectedAppNameState(appName);
        setIsSidebarCollapsed(isDashgenAppName(appName));
        // Clear current session — the session list already shows all agents so no reload needed
        currentSessionRef.current = null;
        setCurrentSession(null);
        setEmbeddedDashboard(null);
        syncSessionToUrl(null);
        hasReceivedFinalMessageRef.current = true;
        setHasReceivedFinalMessage(true);
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, [syncSessionToUrl]);

    useEffect(() => {
        chatApiStore.listApps().then(agents => {
            if (agents.length > 0) setAvailableAgents(agents);
        });
    }, [chatApiStore]);

    const generateId = () => Math.random().toString(36).substring(2, 9);

    const loadMoreSessions = useCallback(async () => {
        if (!hasNextPage || isFetchingNextPage) return;
        await fetchNextPage();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    /** Refresh sidebar titles from the paginated session list without clearing messages. */
    const refreshSessionDisplayNames = useCallback(async () => {
        if (!bagStore.bagId) return;
        try {
            await queryClient.invalidateQueries({queryKey: sessionListQueryKey(bagStore.bagId)});
        } catch (error) {
            console.error('[useChat] Error refreshing session display names:', error);
        }
    }, [bagStore.bagId, queryClient]);

    const patchSessionListCache = useCallback((
        updater: (data: InfiniteData<SessionListResponse>) => InfiniteData<SessionListResponse>,
    ) => {
        if (!bagStore.bagId) return;
        queryClient.setQueryData<InfiniteData<SessionListResponse>>(
            sessionListQueryKey(bagStore.bagId),
            (old) => (old ? updater(old) : old),
        );
    }, [bagStore.bagId, queryClient]);

    const updateSessionName = useCallback((sessionId: string, name: string) => {
        setSessions((prev) =>
            prev.map((s) => (s.id === sessionId ? {...s, name} : s)),
        );
        setCurrentSession((prev) =>
            prev?.id === sessionId ? {...prev, name} : prev,
        );
        patchSessionListCache((old) => ({
            ...old,
            pages: old.pages.map((page) => ({
                ...page,
                items: page.items.map((item) =>
                    item.session_id === sessionId ? {...item, display_name: name} : item,
                ),
            })),
        }));
    }, [patchSessionListCache]);

    const removeSession = useCallback((sessionId: string) => {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        patchSessionListCache((old) => {
            const removed = old.pages.some((page) =>
                page.items.some((item) => item.session_id === sessionId),
            );
            return {
                ...old,
                pages: old.pages.map((page) => ({
                    ...page,
                    items: page.items.filter((item) => item.session_id !== sessionId),
                    total: removed ? Math.max(0, page.total - 1) : page.total,
                })),
            };
        });

        if (currentSessionRef.current?.id === sessionId) {
            currentSessionRef.current = null;
            setCurrentSession(null);
            setEmbeddedDashboard(null);
            syncSessionToUrl(null);
            hasReceivedFinalMessageRef.current = true;
            setHasReceivedFinalMessage(true);
        }
    }, [patchSessionListCache, syncSessionToUrl]);

    useEffect(() => {
        if (!bagStore.bagId) return;

        const prevBagId = prevBagIdRef.current;
        const bagChanged = prevBagId !== 0 && prevBagId !== bagStore.bagId;
        prevBagIdRef.current = bagStore.bagId;

        if (bagChanged) {
            lastRestoredUrlSessionIdRef.current = null;
            currentSessionRef.current = null;
            setCurrentSession(null);
            setEmbeddedDashboard(null);
            syncSessionToUrl(null);
            setSelectedAppNameState(CHAT_CONFIG.APP_NAME);
            setIsSidebarCollapsed(false);
            hasReceivedFinalMessageRef.current = true;
            setHasReceivedFinalMessage(true);

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        }
    }, [bagStore.bagId, syncSessionToUrl]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    /**
     * ADK attaches tool state_delta only to the final function_response, not mid-tool SSE.
     * LongRunningFunctionTool is for client-driven resume rounds, not in-loop streaming here.
     * Poll the agent API in-process store while a response is streaming.
     */
    useEffect(() => {
        if (hasReceivedFinalMessage) return;

        let cancelled = false;

        const tick = async () => {
            if (cancelled || hasReceivedFinalMessageRef.current) return;
            const sessionId = currentSessionRef.current?.id;
            if (!sessionId) return;

            const session = sessionsRef.current.find(s => s.id === sessionId);
            if (!session) return;
            const last = session.messages[session.messages.length - 1];
            if (
                !last ||
                last.event_type !== 'function_call' ||
                !CONSOLIDATION_TOOL_NAMES.has(last.content)
            ) {
                return;
            }

            try {
                const appName = session.appName || CHAT_CONFIG.APP_NAME;
                const progress = await chatApiStore.getTailSpendConsolidationProgress(sessionId, appName);
                if (cancelled || progress == null) return;
                setSessions(prev =>
                    prev.map(s => {
                        if (s.id !== sessionId) return s;
                        const idx = findLatestConsolidationCallIndex(s.messages);
                        if (idx < 0) return s;
                        if (progress.stage === 'done') {
                            const nextMsgs = [...s.messages];
                            const nextMsg = {...nextMsgs[idx]};
                            delete nextMsg.toolProgress;
                            nextMsgs[idx] = nextMsg;
                            return {...s, messages: nextMsgs, updated_at: new Date()};
                        }
                        const nextMsgs = [...s.messages];
                        nextMsgs[idx] = {...nextMsgs[idx], toolProgress: progress};
                        return {...s, messages: nextMsgs, updated_at: new Date()};
                    }),
                );
            } catch {
                /* ignore transient poll failures */
            }
        };

        void tick();
        const intervalId = window.setInterval(() => void tick(), 2000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [hasReceivedFinalMessage, chatApiStore]);

    // --- ADK event mapping ---

    const mapADKEventToChatMessages = useCallback((event: ADKEvent): ChatMessage[] => {
        const messages: ChatMessage[] = [];
        if (!event.content?.parts) return messages;

        const isUser = event.author === 'user';
        const invocationId = event.invocationId ?? event.invocation_id;

        for (const part of event.content.parts) {
            if (part.thought) continue;

            if (part.text) {
                messages.push({
                    id: event.id || generateId(),
                    type: isUser ? 'user' : 'bot',
                    content: part.text,
                    event_type: 'text',
                    invocationId,
                    timestamp: event.timestamp ? new Date(event.timestamp * 1000) : new Date(),
                });
            } else if (part.functionCall) {
                messages.push({
                    id: event.id || generateId(),
                    type: 'bot',
                    content: part.functionCall.name,
                    event_type: 'function_call',
                    invocationId,
                    timestamp: event.timestamp ? new Date(event.timestamp * 1000) : new Date(),
                });
            } else if (part.functionResponse) {
                messages.push({
                    id: event.id || generateId(),
                    type: 'bot',
                    content: part.functionResponse.name,
                    event_type: 'function_response',
                    invocationId,
                    timestamp: event.timestamp ? new Date(event.timestamp * 1000) : new Date(),
                    embedded: parseEmbeddedDashboard(part.functionResponse.response),
                });
            }
        }

        return messages;
    }, []);

    const handleStreamingEvent = useCallback((event: ADKEvent, sessionId: string) => {
        if (event.author === 'user') return;

        const delta = getActionsStateDelta(event);
        if (delta) {
            setSessions(prevSessions => {
                const activeSession = prevSessions.find(s => s.id === sessionId);
                if (!activeSession) return prevSessions;
                return prevSessions.map(session => {
                    if (session.id !== sessionId) return session;
                    const patched = applyConsolidationStateDelta(session.messages, delta);
                    if (patched === session.messages) return session;
                    return {...session, messages: patched, updated_at: new Date()};
                });
            });
        }

        // Skip the final non-partial event -- it's a duplicate of all the streamed chunks
        if (event.partial === false) return;

        const newMessages = mapADKEventToChatMessages(event);
        if (newMessages.length === 0) return;

        // A dashgen tool result carrying an embedded dashboard opens/updates the right-side panel.
        const embeddedMsg = findLatestEmbeddedMessage(newMessages);
        if (embeddedMsg?.embedded) {
            const embedded = embeddedMsg.embedded;
            // create/update mutate the dashboard (same embed uuid) — bump the token
            // so the iframe remounts; show_dashboard only reports, no reload needed.
            const isMutation = embeddedMsg.content !== 'show_dashboard';
            setEmbeddedDashboard(prev => ({
                ...embedded,
                refreshToken: prev?.uuid === embedded.uuid
                    ? prev.refreshToken + (isMutation ? 1 : 0)
                    : 0,
            }));
        }

        setSessions(prevSessions => {
            const activeSession = prevSessions.find(s => s.id === sessionId);
            if (!activeSession) return prevSessions;

            return prevSessions.map(session => {
                if (session.id !== sessionId) return session;

                const updatedMessages = [...session.messages];

                for (const msg of newMessages) {
                    if (msg.event_type === 'function_response') {
                        let replaced = false;
                        for (let i = updatedMessages.length - 1; i >= 0; i--) {
                            if (updatedMessages[i].event_type === 'function_call') {
                                updatedMessages[i] = {
                                    ...updatedMessages[i],
                                    isTransitioning: true,
                                    previousContent: updatedMessages[i].content,
                                    previousEventType: 'function_call',
                                };

                                const idx = i;
                                setTimeout(() => {
                                    setSessions(prev =>
                                        prev.map(s => {
                                            if (s.id !== sessionId) return s;
                                            const msgs = [...s.messages];
                                            if (msgs[idx] && msgs[idx].isTransitioning) {
                                                msgs[idx] = {
                                                    ...msgs[idx],
                                                    content: msg.content,
                                                    event_type: 'function_response',
                                                    isTransitioning: false,
                                                    previousContent: undefined,
                                                    previousEventType: undefined,
                                                    timestamp: new Date(),
                                                    invocationId: msgs[idx].invocationId ?? msg.invocationId,
                                                    toolProgress: undefined,
                                                    embedded: msg.embedded,
                                                };
                                            }
                                            return {...s, messages: msgs, updated_at: new Date()};
                                        })
                                    );
                                }, 300);

                                replaced = true;
                                break;
                            }
                        }
                        if (!replaced) {
                            updatedMessages.push(msg);
                        }
                    } else if (msg.event_type === 'text' && msg.type === 'bot') {
                        // Append streaming text to the last bot text message instead of creating a new bubble
                        const lastMsg = updatedMessages[updatedMessages.length - 1];
                        if (lastMsg && lastMsg.type === 'bot' && lastMsg.event_type === 'text') {
                            updatedMessages[updatedMessages.length - 1] = {
                                ...lastMsg,
                                content: lastMsg.content + msg.content,
                                timestamp: msg.timestamp,
                                invocationId: lastMsg.invocationId ?? msg.invocationId,
                            };
                        } else {
                            updatedMessages.push(msg);
                        }
                    } else {
                        updatedMessages.push(msg);
                    }
                }

                return {...session, messages: updatedMessages, updated_at: new Date()};
            });
        });
    }, [mapADKEventToChatMessages]);

    // --- Loading messages from session detail (ADK events) ---

    const loadMessagesFromADKSession = useCallback((session: Pick<ADKSession, 'events'>): {messages: ChatMessage[]; isComplete: boolean} => {
        const messages: ChatMessage[] = [];
        const events: ADKEvent[] = session.events || [];

        for (const event of events) {
            const mapped = mapADKEventToChatMessages(event);

            for (const msg of mapped) {
                if (msg.event_type === 'function_response') {
                    for (let i = messages.length - 1; i >= 0; i--) {
                        if (messages[i].event_type === 'function_call') {
                            messages[i] = {
                                ...msg,
                                id: msg.id,
                            };
                            break;
                        }
                    }
                } else {
                    messages.push(msg);
                }
            }
        }

        return {messages, isComplete: true};
    }, [mapADKEventToChatMessages]);

    const sendMessage = useCallback(async (content: string, bagId?: number) => {
        if (!content.trim()) return;
        if (!hasReceivedFinalMessageRef.current) {
            console.warn('Still waiting on previous response, cannot send new message yet.');
            return;
        }

        bagId = bagId ?? bagStore.bagId;
        if (!bagId) {
            console.error('Cannot send message: bagId is missing');
            return;
        }

        let session = currentSessionRef.current;

        const userMessage: ChatMessage = {
            id: generateId(),
            type: 'user',
            content,
            timestamp: new Date(),
        };

        setHasReceivedFinalMessage(false);
        hasReceivedFinalMessageRef.current = false;

        if (!session) {
            // Create temp session for UI, then create real session
            const tempSession: ChatSession = {
                id: 'temp',
                userId: '',
                name: 'New chat',
                messages: [userMessage],
                created_at: new Date(),
                updated_at: new Date(),
                appName: selectedAppName,
            };

            setSessions(prev => [...prev, tempSession]);
            currentSessionRef.current = tempSession;
            setCurrentSession(tempSession);

            try {
                const preferredAgent = selectedAppName === 'atlas' ? undefined : selectedAppName;
                const {sessionId, userId} = await chatApiStore.createSession(bagId, selectedAppName, preferredAgent);

                if (!sessionId) {
                    const errorMessage: ChatMessage = {
                        id: generateId(),
                        type: 'bot',
                        content: 'Error: Failed to create a chat session. Please try again.',
                        event_type: 'text',
                        timestamp: new Date(),
                    };
                    setSessions(prev => prev.map(s =>
                        s.id === tempSession.id
                            ? {...s, messages: [...s.messages, errorMessage]}
                            : s
                    ));
                    setHasReceivedFinalMessage(true);
                    return;
                }

                const realSession: ChatSession = {
                    id: sessionId,
                    userId: userId,
                    name: tempSession.name,
                    messages: [userMessage],
                    created_at: tempSession.created_at,
                    updated_at: new Date(),
                    appName: selectedAppName,
                };

                setSessions(prev => {
                    const withoutTemp = prev.filter(s => s.id !== tempSession.id);
                    return [...withoutTemp, realSession];
                });
                currentSessionRef.current = realSession;
                setCurrentSession(realSession);
                syncSessionToUrl(sessionId);
                session = realSession;
            } catch (error) {
                const errorMessage: ChatMessage = {
                    id: generateId(),
                    type: 'bot',
                    content: `Error: Failed to create a chat session: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
                    event_type: 'text',
                    timestamp: new Date(),
                };
                setSessions(prev => prev.map(s =>
                    s.id === tempSession.id
                        ? {...s, messages: [...s.messages, errorMessage]}
                        : s
                ));
                setHasReceivedFinalMessage(true);
                return;
            }
        } else {
            setSessions(prev => {
                const updated = prev.map(s =>
                    s.id === session!.id
                        ? {...s, messages: [...s.messages, userMessage], updated_at: new Date()}
                        : s
                );
                const updatedSession = updated.find(s => s.id === session!.id);
                if (updatedSession) {
                    currentSessionRef.current = updatedSession;
                    setCurrentSession(updatedSession);
                }
                return updated;
            });
        }

        // Stream agent response via SSE — use the session's own appName for routing
        try {
            for await (const event of chatApiStore.sendMessageSSE(
                session!.id,
                session!.userId,
                content,
                session!.appName,
            )) {
                handleStreamingEvent(event, session!.id);
            }
        } catch (error) {
            console.error('SSE streaming error:', error);
            const errorMessage: ChatMessage = {
                id: generateId(),
                type: 'bot',
                content: `Error: ${error instanceof Error ? error.message : 'Connection failed'}`,
                event_type: 'text',
                timestamp: new Date(),
            };
            setSessions(prev => prev.map(s =>
                s.id === session!.id
                    ? {...s, messages: [...s.messages, errorMessage], updated_at: new Date()}
                    : s
            ));
        } finally {
            setHasReceivedFinalMessage(true);
            await refreshSessionDisplayNames();
        }
    }, [chatApiStore, bagStore.bagId, selectedAppName, handleStreamingEvent, refreshSessionDisplayNames, syncSessionToUrl]);

    const selectSession = useCallback(async (session: ChatSession) => {
        if (session.id !== 'temp') {
            syncSessionToUrl(session.id);
        }
        setIsSidebarCollapsed(isDashgenAppName(session.appName));
        setIsRestoringSession(true);
        await Promise.resolve();

        try {
            if (session.messages.length > 0) {
                setCurrentSession(session);
                setEmbeddedDashboard(toEmbeddedView(findLatestEmbeddedMessage(session.messages)?.embedded));
                setHasReceivedFinalMessage(!session.isWaitingForResponse);
                return;
            }

            const sessionDetail = await chatApiStore.getSessionDetail(session.id, session.appName);
            const {messages} = loadMessagesFromADKSession(sessionDetail);

            const updatedSession: ChatSession = {
                ...session,
                userId: session.userId || sessionDetail.user_id || '',
                messages,
            };

            setSessions(prev => prev.map(s => s.id === session.id ? updatedSession : s));
            setCurrentSession(updatedSession);
            setEmbeddedDashboard(toEmbeddedView(findLatestEmbeddedMessage(messages)?.embedded));
            setHasReceivedFinalMessage(true);
        } catch (error) {
            console.error('Error loading session messages:', error);
            setCurrentSession(session);
            setHasReceivedFinalMessage(true);
        } finally {
            setIsRestoringSession(false);
        }
    }, [chatApiStore, loadMessagesFromADKSession, syncSessionToUrl]);

    const startNewChat = useCallback((initialMessage?: string, bagId?: number) => {
        syncSessionToUrl(null);
        currentSessionRef.current = null;
        setCurrentSession(null);
        setEmbeddedDashboard(null);
        setSelectedAppNameState(CHAT_CONFIG.APP_NAME);
        hasReceivedFinalMessageRef.current = true;
        setHasReceivedFinalMessage(true);

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        if (initialMessage) {
            setTimeout(() => {
                sendMessage(initialMessage, bagId).then();
            }, 100);
        }
    }, [sendMessage, syncSessionToUrl]);

    const restoreSession = useCallback(async (sessionId: string) => {
        try {
            setIsRestoringSession(true);

            // Find the session in our list to know which app it belongs to
            const knownSession = sessionsRef.current.find(s => s.id === sessionId);
            const appName = knownSession?.appName ?? CHAT_CONFIG.APP_NAME;
            setIsSidebarCollapsed(isDashgenAppName(appName));

            const sessionDetail = await chatApiStore.getSessionDetail(sessionId, appName);
            const {messages} = loadMessagesFromADKSession(sessionDetail);
            const detailUserId = sessionDetail.user_id || '';

            // Build the session BEFORE setSessions: assigning it inside the state
            // updater only works when React evaluates the updater eagerly (empty
            // queue, e.g. page reload) — on client-side navigation the updater is
            // deferred and the restored session was never made current.
            const existingSession = sessionsRef.current.find(s => s.id === sessionId);
            const updatedSession: ChatSession = existingSession
                ? {...existingSession, userId: existingSession.userId || detailUserId, messages}
                : {
                    id: sessionId,
                    userId: detailUserId,
                    name: `Chat ${sessionsRef.current.length + 1}`,
                    messages,
                    created_at: messages.length > 0 ? messages[0].timestamp : new Date(),
                    updated_at: messages.length > 0 ? messages[messages.length - 1].timestamp : new Date(),
                    appName,
                };
            setSessions(prev => prev.some(s => s.id === sessionId)
                ? prev.map(s => s.id === sessionId ? updatedSession : s)
                : [...prev, updatedSession]);

            currentSessionRef.current = updatedSession;
            setCurrentSession(updatedSession);
            setEmbeddedDashboard(toEmbeddedView(findLatestEmbeddedMessage(messages)?.embedded));
            setHasReceivedFinalMessage(true);

            return updatedSession;
        } catch (error) {
            console.error('Error restoring session:', error);
            return null;
        } finally {
            setIsRestoringSession(false);
        }
    }, [chatApiStore, loadMessagesFromADKSession]);

    const clearSessionLoading = useCallback(() => {
        setIsRestoringSession(false);
    }, []);

    // Read session_id from URL on chat route after the session list fetch has settled.
    const isChatRoute = CHAT_PATH_RE.test(location.pathname);

    useEffect(() => {
        if (!isChatRoute || !bagStore.bagId || !sessionsListReady || sessionsPending) {
            return;
        }

        const sessionIdFromUrl = new URLSearchParams(location.search).get('session_id');
        if (!sessionIdFromUrl) {
            lastRestoredUrlSessionIdRef.current = null;
            return;
        }

        if (
            isRestoringSession ||
            lastRestoredUrlSessionIdRef.current === sessionIdFromUrl ||
            currentSessionRef.current?.id === sessionIdFromUrl
        ) {
            return;
        }

        lastRestoredUrlSessionIdRef.current = sessionIdFromUrl;
        void restoreSession(sessionIdFromUrl);
    }, [
        isChatRoute,
        bagStore.bagId,
        location.search,
        sessionsListReady,
        sessionsPending,
        isRestoringSession,
        restoreSession,
    ]);

    // Adjust state during render when query data / bag changes (React-recommended alternative to syncing in an effect).
    if (bagStore.bagId !== syncedBagId) {
        setSyncedBagId(bagStore.bagId);
        setSyncedSessionListData(sessionListData);
        // Cache hit: apply the new bag's pages now. Clearing without this leaves
        // sessions empty because sessionListData identity does not change again.
        if (sessionListData) {
            setSessions(sessionListData.pages.flatMap((page) =>
                page.items.map(mapBackendSession),
            ));
        } else {
            setSessions([]);
        }
    } else if (sessionListData !== syncedSessionListData) {
        setSyncedSessionListData(sessionListData);
        if (!bagStore.bagId) {
            setSessions([]);
        } else if (sessionListData) {
            const serverSessions = sessionListData.pages.flatMap((page) =>
                page.items.map(mapBackendSession),
            );
            setSessions((prev) => {
                const messagesById = new Map(prev.map((s) => [s.id, s.messages] as const));
                const serverIds = new Set(serverSessions.map((s) => s.id));
                const mergedServer = serverSessions.map((fs) => {
                    const existingMessages = messagesById.get(fs.id);
                    return existingMessages?.length ? {...fs, messages: existingMessages} : fs;
                });
                const localOnly = prev.filter((s) => !serverIds.has(s.id));
                return localOnly.length ? [...localOnly, ...mergedServer] : mergedServer;
            });
        }
    }

    if (currentSession) {
        const updatedSession = sessions.find(s => s.id === currentSession.id);
        if (updatedSession && updatedSession !== currentSession) {
            const keepLocalMessages =
                currentSession.messages.length > 0 &&
                updatedSession.messages.length === 0;
            if (!keepLocalMessages) {
                setCurrentSession(updatedSession);
            }
        }
    }

    return {
        sessions,
        currentSession,
        hasReceivedFinalMessage,
        isWaiting,
        isRestoringSession,
        clearSessionLoading,
        availableAgents,
        selectedAppName,
        setSelectedAppName,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        sendMessage,
        selectSession,
        startNewChat,
        restoreSession,
        updateSessionName,
        removeSession,
        loadMoreSessions,
        hasMoreSessions: Boolean(hasNextPage),
        sessionsPending,
        embeddedDashboard,
    };
};
